import { useState } from 'react';
import { Button, Group, Text, Alert, Loader } from '@mantine/core';
import { DateInput } from '@mantine/dates';
import { DataTable } from 'mantine-datatable';
import { IconAlertCircle } from '@tabler/icons-react';
import HttpClient from '../helpers/HttpClient';
import { CalculationResult } from '../types/calculationResult.type';
import { Booking } from '../types/BookingModel';
import dayjs from 'dayjs';

function Calculation() {
  const [startDate, setStartDate] = useState<string | null>(null);
  const [endDate, setEndDate] = useState<string | null>(null);
  const [results, setResults] = useState<CalculationResult[]>([]);
  const [selectedResults, setSelectedResults] = useState<CalculationResult[]>([]);
  const [bookings, setBookings] = useState<Booking[]>([]);
  const [loading, setLoading] = useState(false);
  const [booking, setBooking] = useState(false);
  const [booked, setBooked] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [ran, setRan] = useState(false);

  const run = async () => {
    if (!startDate || !endDate) return;
    setLoading(true);
    setError(null);
    setBooked(false);
    setBookings([]);
    const snapStart = dayjs(startDate).format('YYYY-MM-DD');
    const snapEnd = dayjs(endDate).format('YYYY-MM-DD');
    try {
      const client = new HttpClient<CalculationResult>('/calculation');
      const data = await client.getAll({ startDate: snapStart, endDate: snapEnd });
      setResults(data ?? []);
      setSelectedResults(data ?? []);
      setRan(true);
    } catch (e: any) {
      setError(e.message || 'Fehler bei der Berechnung');
    } finally {
      setLoading(false);
    }
  };

  const book = async () => {
    if (selectedResults.length === 0) return;
    setBooking(true);
    setError(null);
    try {
      const client = new HttpClient<Booking>('/calculation/book');
      const data = await client.post({ items: selectedResults } as any) as any as Booking[];
      setBookings(Array.isArray(data) ? data : []);
      setBooked(true);
    } catch (e: any) {
      setError(e.message || 'Fehler beim Buchen');
    } finally {
      setBooking(false);
    }
  };

  const perPerson = results.reduce<Record<string, { name: string; total: number }>>((acc, r) => {
    if (!acc[r.playerId]) acc[r.playerId] = { name: r.playerName, total: 0 };
    acc[r.playerId].total += r.amount;
    return acc;
  }, {});

  const perAccount = results.reduce<Record<string, { name: string; total: number }>>((acc, r) => {
    const key = r.paymentAccountId?.toString() ?? 'none';
    const name = r.paymentAccountName ?? 'Kein Konto (Standard)';
    if (!acc[key]) acc[key] = { name, total: 0 };
    acc[key].total += r.amount;
    return acc;
  }, {});

  const grandTotal = results.reduce((sum, r) => sum + r.amount, 0);

  return (
    <>
      <h1>Kalkulation</h1>
      <Group mb="md" align="flex-end">
        <DateInput
          valueFormat="DD.MM.YYYY"
          label="Von"
          value={startDate}
          onChange={setStartDate}
          clearable
        />
        <DateInput
          valueFormat="DD.MM.YYYY"
          label="Bis"
          value={endDate}
          onChange={setEndDate}
          clearable
        />
        <Button onClick={run} loading={loading} disabled={!startDate || !endDate}>
          Berechnen
        </Button>
        <Button
          onClick={book}
          loading={booking}
          disabled={selectedResults.length === 0 || booked}
          color="green"
        >
          {booked ? 'Gebucht' : `Buchen (${selectedResults.length})`}
        </Button>
      </Group>

      {error && (
        <Alert icon={<IconAlertCircle />} color="red" mb="md">
          {error}
        </Alert>
      )}

      {loading && <Loader size="md" />}

      {ran && !loading && results.length === 0 && (
        <Text c="dimmed">Keine Ergebnisse für diesen Zeitraum.</Text>
      )}

      {results.length > 0 && (
        <>
          <DataTable
            withTableBorder
            borderRadius="sm"
            withColumnBorders
            striped
            highlightOnHover
            records={results}
            idAccessor={(r) => `${r.playerId}-${r.contractId ?? ''}-${r.matchId ?? ''}-${r.eventDate}-${r.text}`}
            selectedRecords={selectedResults}
            onSelectedRecordsChange={setSelectedResults}
            columns={[
              { accessor: 'playerName', title: 'Person', width: 180 },
              {
                accessor: 'eventDate',
                title: 'Datum',
                width: 110,
                render: ({ eventDate }) => dayjs(eventDate).format('DD.MM.YYYY'),
              },
              { accessor: 'text', title: 'Beschreibung' },
              { accessor: 'count', title: 'Anzahl', width: 80, textAlign: 'right' },
              {
                accessor: 'amount',
                title: 'Betrag',
                width: 110,
                textAlign: 'right',
                render: ({ amount }) =>
                  amount.toLocaleString('de-AT', { style: 'currency', currency: 'EUR' }),
              },
            ]}
          />

          <Text fw={700} size="lg" ta="right" mt="md">
            Gesamt: {grandTotal.toLocaleString('de-AT', { style: 'currency', currency: 'EUR' })}
          </Text>

          <Text fw={600} mt="xl" mb="xs">Zusammenfassung pro Person</Text>
          <DataTable
            withTableBorder
            borderRadius="sm"
            withColumnBorders
            records={Object.values(perPerson).sort((a, b) => a.name.localeCompare(b.name))}
            columns={[
              { accessor: 'name', title: 'Person' },
              {
                accessor: 'total',
                title: 'Gesamt',
                textAlign: 'right',
                render: ({ total }) =>
                  total.toLocaleString('de-AT', { style: 'currency', currency: 'EUR' }),
              },
            ]}
          />

          <Text fw={600} mt="xl" mb="xs">Zusammenfassung pro Konto</Text>
          <DataTable
            withTableBorder
            borderRadius="sm"
            withColumnBorders
            records={Object.values(perAccount).sort((a, b) => a.name.localeCompare(b.name))}
            columns={[
              { accessor: 'name', title: 'Konto' },
              {
                accessor: 'total',
                title: 'Gesamt',
                textAlign: 'right',
                render: ({ total }) =>
                  total.toLocaleString('de-AT', { style: 'currency', currency: 'EUR' }),
              },
            ]}
          />
        </>
      )}

      {bookings.length > 0 && (
        <>
          <Text fw={600} mt="xl" mb="xs">Gebuchte Buchungen ({bookings.length})</Text>
          <DataTable
            withTableBorder
            borderRadius="sm"
            withColumnBorders
            striped
            highlightOnHover
            records={bookings}
            columns={[
              {
                accessor: 'person',
                title: 'Person',
                width: 180,
                render: ({ person }) => person?.firstName && person?.lastName
                  ? `${person.firstName} ${person.lastName}`
                  : '—',
              },
              {
                accessor: 'bookingDate',
                title: 'Datum',
                width: 110,
                render: ({ bookingDate }) => dayjs(bookingDate).format('DD.MM.YYYY'),
              },
              { accessor: 'bookingText', title: 'Beschreibung' },
              {
                accessor: 'bookingAmount',
                title: 'Betrag',
                width: 110,
                textAlign: 'right',
                render: ({ bookingAmount }) =>
                  Number(bookingAmount).toLocaleString('de-AT', { style: 'currency', currency: 'EUR' }),
              },
            ]}
          />
        </>
      )}
    </>
  );
}

export default Calculation;
